import { FiAlertCircle, FiShieldOff } from "react-icons/fi";
import { Tooltip as ReactTooltip } from "react-tooltip";
import useTranslator from "../../hooks/useTranslator";
import { DownloadIcon } from "../../helper/svgHelper";
import {
  getInsuranceReviewTooltip,
  INSURANCE_REPORT_STATUS,
} from "../../Utils/insuranceReportStatus";

const DecisionCard = ({
  decisionTitle,
  decisionStatus,
  combinedIncome,
  combinedEffortRate,
  decisionSummary,
  insurable,
  insurabilityStatus,
  insurabilityErrorCode,
  insurabilityErrorCodes = [],
  recommendedActions,
  children,
  currentLang,
  preFilledInsuranceDocument,
  insurabilityContactOnly = false,
}) => {
  // Determine the status class for styling
  const { _t } = useTranslator();
  const getStatusClass = () => {
    if (typeof decisionStatus === "string") {
      const lowerStatus = decisionStatus.toLowerCase();
      if (lowerStatus === "approved") return "approved";
      if (lowerStatus === "rejected") return "rejected";
      return "conditional";
    }
    return "conditional";
  };

  const statusClass = getStatusClass();
  const resolvedInsurabilityStatus = insurabilityContactOnly
    ? INSURANCE_REPORT_STATUS.UNDER_REVIEW
    : (insurabilityStatus ??
      (insurable === true
        ? INSURANCE_REPORT_STATUS.INSURABLE
        : insurable === false
          ? INSURANCE_REPORT_STATUS.NOT_INSURABLE
          : null));
  const isInsurable =
    resolvedInsurabilityStatus === INSURANCE_REPORT_STATUS.INSURABLE;
  const isUnderReview =
    resolvedInsurabilityStatus === INSURANCE_REPORT_STATUS.UNDER_REVIEW;
  const reviewTooltip = isUnderReview
    ? getInsuranceReviewTooltip({
        errorCode: insurabilityErrorCode,
        errorCodes: insurabilityErrorCodes,
        translate: _t,
      })
    : null;

  return (
    <section className={`linked-report__decision-card ${statusClass}`}>
      <div className="linked-report__decision-title-row">
        <div className="linked-report__decision-title">{decisionTitle}</div>
        {resolvedInsurabilityStatus && (
          <>
            <span
              data-tooltip-id={
                isUnderReview && !insurabilityContactOnly
                  ? "insurance-insurability-tooltip"
                  : undefined
              }
              style={{
                color: isInsurable
                  ? "#82CF3F"
                  : isUnderReview
                    ? "#737F7B"
                    : "#FF3C3C",
                backgroundColor: isInsurable
                  ? "#F2FBE9"
                  : isUnderReview
                    ? "#F3F4F4"
                    : "#FFE2E2",
                border: isInsurable
                  ? "none"
                  : isUnderReview
                    ? "1px solid #D9DEDC"
                    : "1px solid #EB9B9C",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                cursor:
                  isUnderReview && !insurabilityContactOnly
                    ? "help"
                    : "default",
              }}
              className="report-overview__badge"
            >
              {isInsurable ? (
                <img src={"/images/shield.svg"} alt="" />
              ) : isUnderReview ? (
                <FiAlertCircle
                  className="report-overview__badge-icon"
                  size={14}
                />
              ) : (
                <FiShieldOff
                  className="report-overview__badge-icon"
                  size={14}
                />
              )}
              {isInsurable ? (
                _t("insurable")
              ) : isUnderReview ? (
                insurabilityContactOnly ? (
                  <>
                    {_t("insurability", "Insurability")}:{" "}
                    <a
                      href="https://wa.me/34617662602"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      style={{ color: "inherit", textDecoration: "underline" }}
                    >
                      {_t("contactUs", "Contact us")}
                    </a>
                  </>
                ) : (
                  _t("insurabilityUnderReview", "Insurability under review")
                )
              ) : (
                _t("notInsurable")
              )}
              {isInsurable && preFilledInsuranceDocument && (
                <a
                  href={`${preFilledInsuranceDocument}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="download-icon-wrapper"
                  style={{ color: "inherit", lineHeight: 0 }}
                >
                  <DownloadIcon />
                </a>
              )}
            </span>
            {isUnderReview && !insurabilityContactOnly && (
              <ReactTooltip
                id="insurance-insurability-tooltip"
                place="bottom"
                noArrow
                className="react-tooltip-styling insurance-insurability-tooltip"
                render={() => (
                  <div>
                    {reviewTooltip.title && (
                      <p className="insurance-insurability-tooltip-title">
                        <span>{reviewTooltip.title}</span>
                      </p>
                    )}
                    <p>{reviewTooltip.description}</p>
                  </div>
                )}
              />
            )}
          </>
        )}
      </div>
      <div className="linked-report__decision-metrics">
        <strong>{`€${Math.round(combinedIncome || 0)}/${_t("mo")}`}</strong>
        <span>•</span>
        <strong>{`${_t("effortRate")} ${combinedEffortRate}`}</strong>
      </div>
      {decisionSummary && (
        <p className="linked-report__decision-description">
          {typeof decisionSummary === "string"
            ? decisionSummary
            : decisionSummary[currentLang]?.split("\n")?.join(" ") ||
              "This tenant nearly meets the criteria for automatic approval."}
        </p>
      )}
      {children}
    </section>
  );
};

export default DecisionCard;
